/** A sessão partilhada "actual" só vale enquanto está aberta.
 *
 *  A marca (`sessaoPartilhadaEstado`, no localStorage) ficava depois de a
 *  sessão deixar de estar aberta: o Rui, a 16/10/2026, sem nenhum IFC
 *  aberto, via na barra de cima "CHA139 · só você" (a página tinha sido
 *  recarregada e o trabalho descartado, ou os modelos fechados um a um).
 *
 *  Regras:
 *  - mostra-se só com alguma coisa aberta (modelos ou BCF) ou com a
 *    sessão a abrir;
 *  - esquece-se ao arrancar se não se recuperou trabalho e não há nada
 *    aberto nem a abrir (nada daquela sessão vai voltar);
 *  - esquece-se quando se fecha tudo o que estava aberto.
 */
import { useEffect, useRef } from 'react'
import { useAppStore } from '@core/store'
import { whenRecoveryResolved } from '@core/session/recovery'
import {
  esquecerSessaoActual,
  lerSessaoActual,
  useProgressoDaSessao,
  useSessaoActual,
  type SessaoActual,
} from './sessaoPartilhadaEstado'

export interface EstadoDaVista {
  /** Há modelos ou BCF abertos. */
  temAlgo: boolean
  /** A sessão partilhada está a abrir (há progresso). */
  aAbrir: boolean
}

/** A sessão actual, se de facto aberta agora. */
export function sessaoAberta(actual: SessaoActual | null, e: EstadoDaVista): SessaoActual | null {
  return actual && (e.temAlgo || e.aAbrir) ? actual : null
}

/** Ao arrancar: esquecer a marca? Só se não se recuperou trabalho e não
 *  há nada aberto nem a abrir. */
export function esquecerAoArrancar(recuperou: boolean, e: EstadoDaVista): boolean {
  return !recuperou && !e.temAlgo && !e.aAbrir
}

/** Ao fechar: esquecer a marca quando se passa de ter alguma coisa aberta
 *  para não ter nada, sem a sessão estar a abrir. */
export function esquecerAoFechar(antes: EstadoDaVista, agora: EstadoDaVista): boolean {
  return antes.temAlgo && !agora.temAlgo && !agora.aAbrir
}

/** A sessão partilhada aberta agora (ou null), e a vigia que esquece a
 *  marca quando ela já não está aberta. Usar num só sítio montado sempre
 *  (o indicador da barra de cima); os outros leem `useSessaoAbertaAgora`. */
export function useVigiaDaSessaoPartilhada(workspaceId: string | null | undefined): SessaoActual | null {
  const actual = useSessaoActual(workspaceId)
  const temAlgo = useAppStore((s) => s.models.length > 0 || s.bcfLoaded.length > 0)
  const aAbrir = useProgressoDaSessao() !== null
  const anterior = useRef<EstadoDaVista>({ temAlgo, aAbrir })

  useEffect(() => {
    if (!workspaceId) return
    let vivo = true
    void whenRecoveryResolved().then((recuperou) => {
      if (!vivo || !lerSessaoActual(workspaceId)) return
      const s = useAppStore.getState()
      const agora = { temAlgo: s.models.length > 0 || s.bcfLoaded.length > 0, aAbrir: anterior.current.aAbrir }
      if (esquecerAoArrancar(recuperou, agora)) esquecerSessaoActual()
    })
    return () => {
      vivo = false
    }
  }, [workspaceId])

  useEffect(() => {
    const agora = { temAlgo, aAbrir }
    if (esquecerAoFechar(anterior.current, agora) && actual) esquecerSessaoActual()
    anterior.current = agora
  }, [temAlgo, aAbrir, actual])

  return sessaoAberta(actual, { temAlgo, aAbrir })
}

/** Para quem só lê (o menu Sessão): a sessão partilhada, se aberta. */
export function useSessaoAbertaAgora(workspaceId: string | null | undefined): SessaoActual | null {
  const actual = useSessaoActual(workspaceId)
  const temAlgo = useAppStore((s) => s.models.length > 0 || s.bcfLoaded.length > 0)
  const aAbrir = useProgressoDaSessao() !== null
  return sessaoAberta(actual, { temAlgo, aAbrir })
}
